// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import { useState, useEffect } from "react"
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormLabel,
  Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import { DatePicker } from "@/components/ui/date-picker"
import { CheckCircle } from "lucide-react"

interface Invoice {
  id: string | number
  invoice_number: string
  total_amount: number
}

interface MarkAsPaidModalProps {
  isOpen: boolean
  onClose: () => void
  invoice: Invoice | null
  onConfirm: (invoiceId: string, paidDate: string) => Promise<void>
  isLoading?: boolean
}

export function MarkAsPaidModal({
  isOpen,
  onClose,
  invoice,
  onConfirm,
  isLoading = false
}: MarkAsPaidModalProps) {
  const [paidDate, setPaidDate] = useState("")

  // Set default date to today when modal opens
  useEffect(() => {
    if (isOpen) {
      setPaidDate(new Date().toISOString().split('T')[0])
    }
  }, [isOpen])

  const handleConfirm = async () => {
    if (!invoice || !paidDate) return
    await onConfirm(invoice.id.toString(), paidDate)
  }

  const handleClose = () => {
    if (isLoading) return // Don't allow closing while processing
    onClose()
  }

  if (!invoice) return null

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: 40,
              width: 40,
              borderRadius: "50%",
              bgcolor: (t) => alpha(t.palette.success.main, 0.12),
              color: "success.main",
            }}
          >
            <CheckCircle size={20} />
          </Box>
          <Box>
            <Typography component="div" sx={{ fontSize: "1.125rem", fontWeight: 600, color: "text.primary" }}>
              Mark as Paid
            </Typography>
            <Typography sx={{ fontSize: 14, color: "text.secondary", mt: 0.5 }}>
              Invoice #{invoice.invoice_number || 'N/A'}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Invoice details */}
          <Box sx={{ bgcolor: "surfaceSoft", borderRadius: 1, p: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography component="span" sx={{ fontSize: 14, fontWeight: 500, color: "text.secondary" }}>Invoice Amount</Typography>
              <Typography component="span" sx={{ fontSize: "1.125rem", fontWeight: 600, color: "text.primary" }}>
                ${invoice.total_amount?.toLocaleString() || '0.00'}
              </Typography>
            </Box>
          </Box>

          {/* Payment date */}
          <Box>
            <FormLabel sx={{ display: "block", mb: 0.5 }}>Payment Date *</FormLabel>
            <DatePicker
              value={paidDate}
              onChange={(date) => setPaidDate(date)}
              disabled={isLoading}
            />
            <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
              Select the date when this invoice was actually paid
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 2 }}>
          <Button
            type="button"
            variant="outlined"
            color="inherit"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            onClick={handleConfirm}
            disabled={isLoading || !paidDate}
            sx={{ minWidth: 120 }}
            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <CheckCircle size={16} />}
          >
            {isLoading ? "Processing..." : "Mark as Paid"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
